import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { VariantColor } from '@repo/toolkit/colors'
import { DateFormatter } from '@repo/toolkit/date'
import { StringFormatter } from '@repo/toolkit/string'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import DataTable, { Column } from '@repo/ui/data-table'
import Loader from '@repo/ui/loader'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import { getHostFamilyStudentRatings } from '../../../api/end-of-journey-reviews/host-family/get-host-family-student-ratings'
import {
  HostFamilyRatingAverage,
  HostFamilyScaledRating,
  HostFamilyStudentRatings
} from '../../../api/end-of-journey-reviews/host-family/types'
import { EndOfJourneyColumnEnum } from '../../../lib/types/enums/end-of-stay-template/end-of-journey-column.enum'
import ActionTableWrapper from '../../elements/action-table-wrapper'
import {
  formatReviewNumber,
  ReviewLoadError
} from '../ols/centers/center-tab-views/center-end-of-stay-appreciations/review-rating-pill'

// Beyond this number of students, the rest of the list is behind a "See more" link
const VISIBLE_STUDENTS_COUNT = 10

type Props = {
  id: number
  // the OL space is in English, the LEC space in French
  isEnglish?: boolean
}

type Row = HostFamilyStudentRatings & { id: number }

// Header and subtitle of each rating column the backend sends, as on the Figma
const COLUMN_HEADERS: Partial<
  Record<EndOfJourneyColumnEnum, { en: string; fr: string; infoEn: string; infoFr: string }>
> = {
  [EndOfJourneyColumnEnum.HF_WELCOME]: {
    en: 'Welcome',
    fr: 'Accueil',
    infoEn: '(How well they were received)',
    infoFr: '(Comment il a été reçu)'
  },
  [EndOfJourneyColumnEnum.ACCOMMODATION]: {
    en: 'Accommodation',
    fr: 'Hébergement',
    infoEn: '(Where they stayed)',
    infoFr: '(Où il a logé)'
  },
  [EndOfJourneyColumnEnum.FOOD]: {
    en: 'Food',
    fr: 'Repas',
    infoEn: '(The meals provided)',
    infoFr: '(Les repas fournis)'
  },
  [EndOfJourneyColumnEnum.HF_FAMILY_LIFE]: {
    en: 'Family life',
    fr: 'Vie de famille',
    infoEn: '(Taking part in family life)',
    infoFr: '(Participation à la vie de famille)'
  },
  [EndOfJourneyColumnEnum.HF_TUITION]: {
    en: 'Lessons',
    fr: 'Cours',
    infoEn: '(Lessons given by the teacher)',
    infoFr: '(Cours donnés par le professeur)'
  }
}

// Subtitle under a grade column title, as on the Figma
const GradeHeaderInfo = ({ text }: { text: string }) => (
  <Typography variant="label-medium" color={tailwindColors.grey.lighter}>
    {text}
  </Typography>
)

// A rating on its own scale: x/5 or x/3, never converted
const GradeBadge = ({
  grade,
  variant
}: {
  grade: HostFamilyScaledRating | HostFamilyRatingAverage | null | undefined
  variant: VariantColor
}) => {
  if (!grade) return '-'
  // Averages as on the other review screens; a youth's own rating is a whole number
  const value = 'average' in grade ? formatReviewNumber(grade.average) : grade.rating
  return <Badge variant={variant} text={`${value}/${grade.scaleMax}`} />
}

const HostFamilyStudentAppreciations = ({ id, isEnglish }: Props) => {
  const [showAll, setShowAll] = useState(false)

  const { data, isPending, error } = useQuery({
    queryKey: ['host-family-student-ratings', id],
    queryFn: () => getHostFamilyStudentRatings(id),
    enabled: !!id
  })

  const students = data?.students ?? []
  const visibleStudents = showAll ? students : students.slice(0, VISIBLE_STUDENTS_COUNT)
  const ratingColumns = data?.columns ?? []

  const columns: Column<Row>[] = [
    {
      header: isEnglish ? 'First name' : 'Prénom',
      accessorKey: 'firstName',
      cell: ({ row }) => StringFormatter.capitalize(row.original.firstName ?? '-')
    },
    {
      header: isEnglish ? 'Last name' : 'Nom',
      accessorKey: 'lastName',
      cell: ({ row }) => (row.original.lastName ?? '-').toUpperCase()
    },
    {
      header: isEnglish ? 'Date of stay' : 'Dates du séjour',
      accessorKey: 'startDate',
      cell: ({ row }) => {
        const { startDate, endDate } = row.original
        if (!startDate || !endDate) return '-'
        return <Badge text={DateFormatter.getDateRangeDDMMYYYY(startDate, endDate)} />
      }
    },
    ...ratingColumns.map(({ code }): Column<Row> => {
      const header = COLUMN_HEADERS[code]
      return {
        header: (isEnglish ? header?.en : header?.fr) ?? code,
        accessorKey: `ratings.${code}` as Column<Row>['accessorKey'],
        headerInfo: header && <GradeHeaderInfo text={isEnglish ? header.infoEn : header.infoFr} />,
        cell: ({ row }) => <GradeBadge grade={row.original.ratings[code]} variant="primary" />
      }
    })
  ]

  return (
    <ActionTableWrapper
      title={isEnglish ? 'Students appreciations' : 'Appréciations des étudiants'}
      actions={
        data?.average
          ? [
              <div key="average" className="flex items-center gap-2">
                <Typography variant="label-medium" color={tailwindColors.grey.lighter}>
                  {isEnglish ? 'Host family rating' : 'Note de la famille'}
                </Typography>
                <GradeBadge grade={data.average} variant="success" />
              </div>
            ]
          : undefined
      }
    >
      {isPending ? (
        <Loader center />
      ) : error ? (
        <ReviewLoadError error={error} />
      ) : students.length === 0 ? (
        <Typography variant="tag" color={tailwindColors.grey.light.active}>
          {isEnglish ? 'No student hosted yet' : 'Aucun étudiant hébergé pour le moment'}
        </Typography>
      ) : (
        <>
          <DataTable<Row>
            manualSorting={false}
            columns={columns}
            data={visibleStudents.map((student) => ({ ...student, id: student.enrollmentId }))}
            rowCount={visibleStudents.length}
            paginated={false}
            footerCells={[
              {
                accessorKey: 'firstName',
                colSpan: 3,
                value: (
                  <Typography variant="label-semibold" color={tailwindColors.primary.DEFAULT}>
                    {isEnglish ? 'Average Rating' : 'Note moyenne'}
                  </Typography>
                )
              },
              ...ratingColumns.map(({ code }) => ({
                accessorKey: `ratings.${code}` as Column<Row>['accessorKey'],
                value: <GradeBadge grade={data?.averages[code]} variant="success" />
              }))
            ]}
          />
          {!showAll && students.length > VISIBLE_STUDENTS_COUNT && (
            <Button
              type="button"
              variant="ghost"
              className="w-fit"
              label={isEnglish ? 'See more' : 'Voir plus'}
              onClick={() => setShowAll(true)}
            />
          )}
        </>
      )}
    </ActionTableWrapper>
  )
}

export default HostFamilyStudentAppreciations
